import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Dialog, Button } from "@material-ui/core";
import "./ImportPDFPage.scss";
import { showErrorToast } from "../components/toastComponent/ToastComponent";
import CustomTooltip from "../components/Tooltip/Tooltip";
import { ReactComponent as DashboardImage } from "../images/home-icon.svg";
import CoverIcon from "../images/cover.svg";
import BlueCheckBox from "../images/bluecheckbox.svg";
import GreenTickMark from "../images/Final-Status.svg";
import NotApprovedImage from "../images/not-approved-image.svg";
import time from "../images/time.svg";
import previewEyeIcon from "../images/preview-eye-icon.svg";
import ModuleStepper from "../components/ModuleStepper/ModuleStepper";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  getVeevaApprovedDocs,
  getVeevaPdfFile,
  getBrandDetails,
  getMarketDetails,
  getIndicationDetails,
} from "../services/apis";
import CircularProgressBar from "../utils/CircularProgressBar";
import NoResultsFound from "../images/Noresults.svg";
import { SearchTextField } from "../components/ResuableComponent/CustomModal/TextFields/TextFields";
import SelectAutoComplete from "../components/ResuableComponent/SelectAutoComplete/SelectAutoComplete";
import MultiSelectDropdown from "../components/ResuableComponent/MultiSelectDropdown/MultiSelectDropdown";
import { daysAgoCreated } from "../utils";
import ConfirmationModal from "../components/template/confirmationTemplate";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Icons
const HomeIcon = () => <DashboardImage width="20" height="20" />;

const ImportPDFPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [edetailName, setEdetailName] = useState("eDetail_01");
  const [currentStep] = useState(1);
  const infinteScrollRef = useRef(null);
  const isRestoringRef = useRef(false);

  // PromoMats states
  const [selectedPromoPdf, setSelectedPromoPdf] = useState(null);
  const [pdfList, setPdfList] = useState([]);
  const [allPdfList, setAllPdfList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [mlrStatusFilter] = useState("Approved for Use"); // Locked to "Approved for Use"
  const [pdfThumbnails, setPdfThumbnails] = useState({}); // Store PDF URLs for thumbnails
  const [hoveredPdfId, setHoveredPdfId] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfName, setPreviewPdfName] = useState("");
  const [previewTotalPages, setPreviewTotalPages] = useState(0);
  const [visiblePreviewPages, setVisiblePreviewPages] = useState(3);
  const [previewPdfDocumentNumber, setPreviewPdfDocumentNumber] = useState("");
  const [previewPdfProduct, setPreviewPdfProduct] = useState("");
  const [previewPdfCountry, setPreviewPdfCountry] = useState("");
  const [previewPdfStatus, setPreviewPdfStatus] = useState("");

  // Dropdown data states
  const [brandsList, setBrandsList] = useState([]);
  const [marketsList, setMarketsList] = useState([]);
  const [indicationsList, setIndicationsList] = useState([]);

  // Selected filter values (applied filters)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedIndication, setSelectedIndication] = useState(null);

  // Temporary filter values (before applying)
  const [tempSelectedProduct, setTempSelectedProduct] = useState(null);
  const [tempSelectedCountry, setTempSelectedCountry] = useState(null);
  const [tempSelectedIndication, setTempSelectedIndication] = useState(null);
  const [isFiltersRestored, setIsFiltersRestored] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Compute reset button state based on applied filters - no separate state needed
  const enableResetBtn = useMemo(() => {
    return !!(
      selectedProduct ||
      selectedCountry ||
      selectedIndication ||
      searchText.trim().length > 0
    );
  }, [
    selectedProduct,
    selectedCountry,
    selectedIndication,
    searchText,
  ]); // For multiple selections

  const steps = [
    "Search in PromoMats",
    "Preview & Convert to HTML",
    "Publish HTML",
  ];

  useEffect(() => {
    if (!location.state?.filters || !location.state?.selectedPdf) return;
    if (!brandsList.length || !marketsList.length || !indicationsList.length) return;

    // Wait until indications are available if needed
    const needsIndications = Boolean(location.state.filters.indication);
    if (needsIndications && !indicationsList.length) return;

    if (isRestoringRef.current) return;
    isRestoringRef.current = true;

    const { filters, selectedPdf } = location.state;

    // ---- Restore PDF ----
    setSelectedPromoPdf(selectedPdf);

    // ---- Restore Product ----
    if (filters.product) {
      const restoredProduct =
        brandsList.find(p => p._id === filters.product._id) || null;

      setSelectedProduct(restoredProduct);
      setTempSelectedProduct(restoredProduct);
    } else {
      setSelectedProduct(null);
      setTempSelectedProduct(null);
    }

    // ---- Restore Country ----
    if (filters.country) {
      const restoredCountry =
        marketsList.find(c => c._id === filters.country._id) || null;

      setSelectedCountry(restoredCountry);
      setTempSelectedCountry(restoredCountry);
    } else {
      setSelectedCountry(null);
      setTempSelectedCountry(null);
    }

    // ---- Restore Indication(s) ----
    if (filters.indication) {
      const restoredIndication =
        indicationsList.find(i => i._id === filters.indication._id) || null;
      setSelectedIndication(restoredIndication);
      setTempSelectedIndication(restoredIndication)
    } else {
      setSelectedIndication(null);
      setTempSelectedIndication(null);
    }

    setIsFiltersRestored(true);
    isRestoringRef.current = false
    navigate("/import-pdf", { replace: true, state: null });
  }, [location.state, brandsList, marketsList, indicationsList]);
  // Get product and country from localStorage - same as ModuleContent.js
  // let localdata = JSON.parse(localStorage.getItem("basicEdetailData")) || {};

  // Fetch dropdown data on mount and set initial values from localStorage
  useEffect(() => {
    const currentLocaldata =
      JSON.parse(localStorage.getItem("basicEdetailData")) || {};

    // Fetch brands
    getBrandDetails((response, err) => {
      console.log("Brands API Response:", response, err);
      if (!err && response?.data) {
        console.log("Setting brands list:", response.data);
        setBrandsList(response.data);

        // Set initial product from localStorage if available
        if (currentLocaldata?.brand?._id) {
          const initialProduct = response.data.find(
            (item) => item._id === currentLocaldata.brand._id
          );
          if (initialProduct) {
            console.log(
              "Setting initial product from localStorage:",
              initialProduct
            );
            setSelectedProduct(initialProduct);
            setTempSelectedProduct(initialProduct);
          }
        }
      } else {
        console.error("Error fetching brands:", err);
      }
    });

    // Fetch markets/countries
    getMarketDetails((response, err) => {
      console.log("Markets API Response:", response, err);
      if (!err && response?.data) {
        console.log("Setting markets list:", response.data);
        setMarketsList(response.data);

        // Set initial country from localStorage if available
        if (currentLocaldata?.market?._id) {
          const initialCountry = response.data.find(
            (item) => item._id === currentLocaldata.market._id
          );
          if (initialCountry) {
            console.log(
              "Setting initial country from localStorage:",
              initialCountry
            );
            setSelectedCountry(initialCountry);
            setTempSelectedCountry(initialCountry);
          }
        }
      } else {
        console.error("Error fetching markets:", err);
      }
    });

    // Fetch indications
    getIndicationDetails((response, err) => {
      console.log("Indications API Response:", response, err);
      if (!err && response?.data) {
        console.log("Setting indications list:", response.data);
        setIndicationsList(response.data);

        // Set initial indication from localStorage if available
        if (currentLocaldata?.indication) {
          const initialIndication = response.data.find(
            (item) => item._id === currentLocaldata.indication
          );
          if (initialIndication) {
            console.log(
              "Setting initial indication from localStorage:",
              initialIndication
            );
            setSelectedIndication(initialIndication);
            setTempSelectedIndication(initialIndication);
          }
        }
      } else {
        console.error("Error fetching indications:", err);
      }
    });
  }, []);


  // Fetch veeva approved docs
  const fetchVeevaDocs = React.useCallback(
    (reset = false) => {
      // Build query params from selected filters
      const queryParams = {};
      if (selectedProduct?._id) queryParams.product = selectedProduct._id;
      if (selectedCountry?._id) queryParams.country = selectedCountry._id;
      if (selectedIndication?._id) queryParams.indication = selectedIndication._id;
      if (searchText) queryParams.docName = searchText;

      // Call API even without product/country on mount
      // if (!queryParams.product || !queryParams.country) {
      //   setLoading(false);
      //   console.warn("Product and Country are required", queryParams);
      //   return;
      // }

      console.log("Calling getVeevaApprovedDocs with params:", queryParams);
      console.log(
        "API URL will be: pdf-html/veeva-approved-docs?" +
        new URLSearchParams(queryParams).toString()
      );
      setLoading(true);
      getVeevaApprovedDocs(queryParams, (response, err, errorCode) => {
        console.log("getVeevaApprovedDocs callback:", {
          response,
          err,
          errorCode,
        });
        setLoading(false); // Always set loading to false when API call completes
        if (err) {
          console.error("Error fetching veeva docs:", err, errorCode);
          showErrorToast("Failed to fetch PDF documents");
          setPdfList([]);
          setAllPdfList([]);
        } else {
          console.log("API Response:", response);
          // New API response structure: { success: true, response: { veevaApprovedDocs: [...], responseDetails: {...} }, err: null }
          const data = response?.data?.response?.veevaApprovedDocs || [];
          console.log("Fetched PDFs:", data.length, data);
          setAllPdfList(data);
          // Always apply MLR filter (locked to "Approved for Use")
          const filtered = data.filter(
            (pdf) => pdf.docStatus === mlrStatusFilter
          );
          console.log(
            "Filtered by MLR status:",
            filtered.length,
            "from",
            data.length
          );
          if (reset) {
            setPdfList(filtered);
          } else {
            setPdfList((prev) => [...prev, ...filtered]);
          }
          setHasMore(false); // API returns all data at once
        }
      });
    },
    [
      selectedProduct,
      selectedCountry,
      selectedIndication,
      searchText,
      mlrStatusFilter,
    ]
  );

  // Restore selected PDF when coming back from preview
  // useEffect(() => {
  //   // Only restore if we have selectedPdf in location.state and we're not already restoring
  //   // Also check that location.state is not empty (which happens after reset)
  //   if (
  //     location.state?.selectedPdf &&
  //     !isRestoringRef.current &&
  //     Object.keys(location.state).length > 0
  //   ) {
  //     isRestoringRef.current = true;
  //     const pdf = location.state.selectedPdf;
  //     setSelectedPromoPdf(pdf);

  //     // Restore filters if provided - set immediately so they show in UI
  //     if (location.state.filters) {
  //       if (location.state.filters.product) {
  //         setSelectedProduct(location.state.filters.product);
  //         setTempSelectedProduct(location.state.filters.product);
  //       } else {
  //         setSelectedProduct(null);
  //         setTempSelectedProduct(null);
  //       }

  //       if (location.state.filters.country) {
  //         setSelectedCountry(location.state.filters.country);
  //         setTempSelectedCountry(location.state.filters.country);
  //       } else {
  //         setSelectedCountry(null);
  //         setTempSelectedCountry(null);
  //       }

  //       if (location.state.filters.indication) {
  //         // Set immediately so it shows in the dropdown
  //         const indicationToRestore = location.state.filters.indication;
  //         // Support both single and array formats
  //         if (
  //           Array.isArray(indicationToRestore) &&
  //           indicationToRestore.length > 0
  //         ) {
  //           setSelectedIndications(indicationToRestore);
  //           setTempSelectedIndications(indicationToRestore);
  //           setSelectedIndication(indicationToRestore[0] || null);
  //           setTempSelectedIndication(indicationToRestore[0] || null);
  //         } else if (indicationToRestore) {
  //           // Single indication object
  //           setSelectedIndications([indicationToRestore]);
  //           setTempSelectedIndications([indicationToRestore]);
  //           setSelectedIndication(indicationToRestore);
  //           setTempSelectedIndication(indicationToRestore);
  //         }
  //       } else {
  //         // Clear indication if not in filters
  //         setSelectedIndication(null);
  //         setSelectedIndications([]);
  //         setTempSelectedIndication(null);
  //         setTempSelectedIndications([]);
  //       }
  //     } else {
  //       // No filters to restore - clear all
  //       setSelectedProduct(null);
  //       setSelectedCountry(null);
  //       setSelectedIndication(null);
  //       setSelectedIndications([]);
  //       setTempSelectedProduct(null);
  //       setTempSelectedCountry(null);
  //       setTempSelectedIndication(null);
  //       setTempSelectedIndications([]);
  //     }

  //     // Fetch data with restored filters - use next tick to ensure state updates are applied
  //     // Check if indicationsList is needed
  //     const hasIndicationFilter = location.state.filters?.indication;
  //     if (indicationsList.length > 0 || !hasIndicationFilter) {
  //       // If indicationsList is loaded or no indication to restore, proceed immediately
  //       // Use next tick to ensure state updates are applied
  //       requestAnimationFrame(() => {
  //         isRestoringRef.current = false;
  //         fetchVeevaDocs(true);
  //       });
  //     } else {
  //       // Wait for indicationsList to load - check once
  //       const checkOnce = () => {
  //         requestAnimationFrame(() => {
  //           isRestoringRef.current = false;
  //           fetchVeevaDocs(true);
  //         });
  //       };
  //       // Use requestAnimationFrame for efficient timing
  //       requestAnimationFrame(checkOnce);
  //     }
  //   }
  // }, [location.state, fetchVeevaDocs, indicationsList]);

  // Update indication when indicationsList loads to match with list item (silently, without re-fetching)
  // This only runs to sync the indication object with the full list data, not to clear values

  // Call API on component mount - fetch all by default
  useEffect(() => {
    console.log("Component mounted - fetching all PDFs by default");
    // Only fetch if not restoring (restoration will trigger its own fetch)
    if (!location.state?.selectedPdf && !isRestoringRef.current) {
      fetchVeevaDocs(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter changes - trigger API call (but not during restoration)
  // Note: This is handled by applyFilters callback, so we don't need this useEffect
  // It was causing unnecessary re-fetches when restoring


  // useEffect(() => {
  //   if (searchText === "" && !location.state) {
  //     fetchVeevaDocs(true);
  //   }
  // }, [searchText]);

  // Apply MLR status filter to already fetched data (always locked to "Approved for Use")
  useEffect(() => {
    console.log("MLR filter effect:", {
      mlrStatusFilter,
      allPdfListLength: allPdfList.length,
    });
    const filtered = allPdfList.filter(
      (pdf) => pdf.docStatus === mlrStatusFilter
    );
    console.log("Filtered PDFs by MLR status:", filtered.length);
    setPdfList(filtered);
  }, [mlrStatusFilter, allPdfList]);

  // Check if file is PDF
  const isPdfFile = useCallback((filename) => {
    if (!filename) return false;
    const extension = filename.toLowerCase().split(".").pop();
    return extension === "pdf";
  }, []);

  // Handle PDF selection
  const handlePromoPdfSelect = useCallback(
    (pdf) => {
      // Check if file is PDF before allowing selection
      if (!isPdfFile(pdf.docFilename)) {
        showErrorToast("This file cannot be selected. It is not a PDF file");
        return;
      }

      if (pdf.docIsLargeFile) {
        showErrorToast("This file cannot be selected. The file size is Large");
        return;
      }
      if (selectedPromoPdf && pdf.docId === selectedPromoPdf?.docId) {
        setSelectedPromoPdf(null)
        return
      }

      setSelectedPromoPdf(pdf);
      setEdetailName(pdf.docName || "eDetail_01");

      // Fetch PDF for thumbnail if not already fetched
      setPdfThumbnails((prev) => {
        if (prev[pdf.docId]) {
          return prev; // Already fetched, no need to fetch again
        }

        // Fetch in background without blocking state update
        const minorVersion = pdf.docMinorVersionNumber || 0;
        const majorVersion = pdf.docMajorVersionNumber || 1;

        getVeevaPdfFile(
          pdf.docId,
          minorVersion,
          majorVersion,
          (response, err, errorCode) => {
            if (err) {
              console.error("Error fetching PDF thumbnail:", err, errorCode);
            } else if (response && response.data) {
              const blob = response.data;
              const url = URL.createObjectURL(blob);
              setPdfThumbnails((prevThumbnails) => {
                // Check again if it was already set
                if (prevThumbnails[pdf.docId]) {
                  URL.revokeObjectURL(url); // Cleanup if duplicate
                  return prevThumbnails;
                }
                return {
                  ...prevThumbnails,
                  [pdf.docId]: url,
                };
              });
            }
          }
        );

        return prev; // Return previous state while fetching
      });
    },
    [isPdfFile, selectedPromoPdf]
  );

  const handleThumbnailHover = useCallback((pdfId) => {
    setHoveredPdfId(pdfId);
  }, []);

  const handleThumbnailLeave = useCallback(() => {
    setHoveredPdfId(null);
  }, []);

  const handleThumbnailClick = useCallback((e, pdf) => {
    e.stopPropagation();
    // Fetch PDF on click of Preview button
    setLoading(true);
    const minorVersion = pdf.docMinorVersionNumber || 0;
    const majorVersion = pdf.docMajorVersionNumber || 1;

    getVeevaPdfFile(
      pdf.docId,
      minorVersion,
      majorVersion,
      (response, err, errorCode) => {
        setLoading(false);
        if (err) {
          console.error("Error fetching PDF for preview:", err, errorCode);
          showErrorToast("Failed to load PDF for preview");
        } else if (response && response.data) {
          // API now returns JSON with fileUrl instead of blob
          let responseData;
          try {
            // Check if response.data is already an object or needs parsing
            if (typeof response.data === "string") {
              responseData = JSON.parse(response.data);
            } else {
              responseData = response.data;
            }
          } catch (parseError) {
            console.error("Error parsing response:", parseError);
            showErrorToast("Invalid response format from server");
            return;
          }

          if (responseData?.response?.fileUrl) {
            setPreviewPdfUrl(responseData.response.fileUrl);
            setPreviewPdfName(pdf.docName || "");
            setPreviewPdfDocumentNumber(pdf.docDocumentNumber)
            setPreviewPdfProduct(pdf.docProductName?.[0] || "");
            setPreviewPdfCountry(pdf.docCountryName?.[0] || "");
            setPreviewPdfStatus(pdf.docStatus || "");
            setShowPdfPreview(true);
          } else {
            console.error("No fileUrl in response:", responseData);
            showErrorToast("Invalid response format from server");
          }
        }
      }
    );
  }, []);

  const closePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prevUrl) => {
      // Only revoke blob URLs, not regular URLs
      if (
        prevUrl &&
        typeof prevUrl === "string" &&
        prevUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(prevUrl);
      }
      return null;
    });
    setPreviewPdfName("");
    setPreviewPdfDocumentNumber("");
    setPreviewPdfProduct("");
    setPreviewPdfCountry("");
    setPreviewPdfStatus("");
    setPreviewTotalPages(0);
    setShowPdfPreview(false);
  }, []);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      // Cleanup all thumbnail URLs on unmount
      setPdfThumbnails((prevThumbnails) => {
        Object.values(prevThumbnails).forEach((url) => {
          if (url && typeof url === "string" && url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
        return {};
      });
    };
  }, []); // Only run on unmount

  // Cleanup preview URL when preview closes
  useEffect(() => {
    if (!showPdfPreview) {
      // Cleanup when preview is closed
      setPreviewPdfUrl((prevUrl) => {
        if (
          prevUrl &&
          typeof prevUrl === "string" &&
          prevUrl.startsWith("blob:")
        ) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    }
  }, [showPdfPreview]);

  useEffect(() => {
    if (!isFiltersRestored) return;
    fetchVeevaDocs(true);
    setIsFiltersRestored(false);
  }, [isFiltersRestored]);

  const handleResetFilters = () => {
    // Set restoring flag to prevent any restoration logic from interfering
    isRestoringRef.current = true;

    // Clear all filters
    setSelectedProduct(null);
    setSelectedCountry(null);
    setSelectedIndication(null);
    setTempSelectedProduct(null);
    setTempSelectedCountry(null);
    setTempSelectedIndication(null);
    // MLR Status remains locked to "Approved for Use"
    setSearchText("");
    // Clear selected PDF card
    setSelectedPromoPdf(null);

    // Clear location.state to prevent restoration from old navigation state
    // Navigate to the same route with empty state to clear location.state
    navigate("/import-pdf", { replace: true, state: {} });

    // Fetch all PDFs directly with empty query params (no filters)
    // This ensures we get all PDFs regardless of state updates
    console.log("Reset filters - fetching all PDFs");
    setLoading(true);
    getVeevaApprovedDocs({}, (response, err, errorCode) => {
      console.log("getVeevaApprovedDocs callback (reset):", {
        response,
        err,
        errorCode,
      });
      setLoading(false);
      isRestoringRef.current = false;
      if (err) {
        console.error("Error fetching veeva docs:", err, errorCode);
        showErrorToast("Failed to fetch PDF documents");
        setPdfList([]);
        setAllPdfList([]);
      } else {
        console.log("API Response (reset):", response);
        const data = response?.data?.response?.veevaApprovedDocs || [];
        console.log("Fetched PDFs (reset):", data.length, data);
        setAllPdfList(data);
        // Always apply MLR filter (locked to "Approved for Use")
        const filtered = data.filter(
          (pdf) => pdf.docStatus === mlrStatusFilter
        );
        console.log(
          "Filtered by MLR status (reset):",
          filtered.length,
          "from",
          data.length
        );
        setPdfList(filtered);
        setHasMore(false); // API returns all data at once
      }
    });
  };

  // Apply filters when a dropdown value is applied
  const applyFilters = useCallback(
    (newProduct, newCountry, newIndication) => {
      // Apply filters to actual state (no validation - can search with any combination)
      setSelectedProduct(newProduct);
      setSelectedCountry(newCountry);
      setSelectedIndication(newIndication);
      // Clear search text when applying new filters
      setSearchText("");

      // Call API with new filters (can be empty to fetch all)
      const queryParams = {};
      if (newProduct?._id) queryParams.product = newProduct._id;
      if (newCountry?._id) queryParams.country = newCountry._id;
      if (newIndication?._id) queryParams.indication = newIndication._id;

      console.log("Calling getVeevaApprovedDocs with params:", queryParams);
      setLoading(true);
      getVeevaApprovedDocs(queryParams, (response, err, errorCode) => {
        console.log("getVeevaApprovedDocs callback:", {
          response,
          err,
          errorCode,
        });
        setLoading(false);
        if (err) {
          console.error("Error fetching veeva docs:", err, errorCode);
          showErrorToast("Failed to fetch PDF documents");
          setPdfList([]);
          setAllPdfList([]);
        } else {
          console.log("API Response:", response);
          const data = response?.data?.response?.veevaApprovedDocs || [];
          console.log("Fetched PDFs:", data.length, data);
          setAllPdfList(data);
          // Always apply MLR filter (locked to "Approved for Use")
          const filtered = data.filter(
            (pdf) => pdf.docStatus === mlrStatusFilter
          );
          const scrollablePromoMats = document.getElementById("scrollableDivPromoMats");
          scrollablePromoMats.scrollTop = 0
          setPdfList(filtered);
          setHasMore(false);
        }
      });

      return true;
    },
    [mlrStatusFilter]
  );

  const handleProductChange = (e, newValue, label) => {
    console.log("Product changed:", newValue);
    setTempSelectedProduct(newValue);
    // Apply filters immediately (no requirement for both product and country)
    applyFilters(newValue, tempSelectedCountry, tempSelectedIndication);
  };

  const handleCountryChange = (e, newValue, label) => {
    console.log("Country changed:", newValue);
    setTempSelectedCountry(newValue);
    // Apply filters immediately (no requirement for both product and country)
    applyFilters(tempSelectedProduct, newValue, tempSelectedIndication);
  };

  const handleIndicationChange = (e, newValue, label) => {
    console.log("Indication changed:", newValue);
    setTempSelectedIndication(newValue);
    // Apply filters immediately (no requirement for both product and country)
    applyFilters(tempSelectedProduct, tempSelectedCountry, newValue);
  };

  const templateListUI = (props, option) => {
    return (
      <li {...props} className="dropdown-list-css">
        <span>{option.name || option}</span>
      </li>
    );
  };

  const handleNext = () => {
    if (selectedPromoPdf) {
      // Get country name
      // Priority: PDF data name -> Dropdown selected name -> PDF data ID mapping -> "-"
      let countryName = selectedPromoPdf.docCountryName?.[0] || "";
      if (!countryName) {
        const pdfCountryCode = selectedPromoPdf.docCountry?.[0];
        const countryFromList = pdfCountryCode
          ? marketsList.find(
            (market) =>
              market.code?.toLowerCase() === pdfCountryCode.toLowerCase() ||
              market.name?.toLowerCase() === pdfCountryCode.toLowerCase()
          )
          : null;
        countryName = countryFromList?.name || pdfCountryCode || "";
      }

      // Get indication name
      let indicationName = selectedPromoPdf.docIndicationName?.[0] || "";
      if (!indicationName) {
        const pdfIndicationId = selectedPromoPdf.docIndication?.[0];
        const indicationFromList = pdfIndicationId
          ? indicationsList.find(
            (ind) =>
              ind._id === pdfIndicationId ||
              ind.id === pdfIndicationId ||
              ind.code === pdfIndicationId
          )
          : null;
        indicationName = indicationFromList?.name || pdfIndicationId || "";
      }

      // Get product name
      const productName =
        selectedPromoPdf.docProductName?.[0] || selectedProduct?.name || "";

      // Resolve Brand ID (Internal ObjectId)
      let resolvedBrandId = selectedProduct?._id;
      if (!resolvedBrandId && productName) {
        const foundBrand = brandsList.find(
          (b) => b.name?.toLowerCase() === productName.toLowerCase()
        );
        resolvedBrandId = foundBrand?._id;
      }

      // Resolve Market ID (Internal ObjectId)
      let resolvedMarketId = selectedCountry?._id;
      if (!resolvedMarketId) {
        // Try matching by name first (most reliable if we have it)
        if (countryName) {
          const foundMarket = marketsList.find(
            (m) => m.name?.toLowerCase() === countryName.toLowerCase()
          );
          resolvedMarketId = foundMarket?._id;
        }

        // Fallback to code matching if still not found
        if (!resolvedMarketId) {
          const pdfCountryCode = selectedPromoPdf.docCountry?.[0];
          if (pdfCountryCode) {
            const foundMarket = marketsList.find(
              (market) =>
                market.code?.toLowerCase() === pdfCountryCode.toLowerCase() ||
                market.name?.toLowerCase() === pdfCountryCode.toLowerCase()
            );
            resolvedMarketId = foundMarket?._id;
          }
        }
      }

      navigate("/preview-convert", {
        state: {
          pdfDocId: selectedPromoPdf.docId,
          edetailName: edetailName,
          minorVersion: selectedPromoPdf.docMinorVersionNumber,
          majorVersion: selectedPromoPdf.docMajorVersionNumber,
          pdf: selectedPromoPdf, // Pass full PDF object for reference
          resolvedIds: {
            brandId: resolvedBrandId,
            marketId: resolvedMarketId
          },
          filters: {
            product: selectedProduct,
            country: selectedCountry,
            indication: selectedIndication,
          },
          displayNames: {
            product: productName,
            country: countryName,
            indication: indicationName,
          },
        },
      });
    }
  };

  // Search handlers
  const searchfieldsHandler = (e) => {
    let value = e.target.value;
    if (value.startsWith(" ")) return;  // value should not start with space
    value = value.replace(/\s{2,}/g, " "); // more that one space is not allowed
    setSearchText(value);
    setPdfList(() => (
      allPdfList.filter(doc => doc.docName.toLowerCase().includes(value.toLowerCase().trim()))
    ));
  };

  const cancelSearchField = () => {
    setSearchText("");
    setPdfList(allPdfList);
  };

  const handleEnterKeypress = (event) => {
    // Reset button state is computed automatically based on searchText
  };

  const searchHandler = () => {
    fetchVeevaDocs(true)
    // Reset button state is computed automatically based on searchText
  };

  return (
    <div className="import-pdf-page">
      {loading && (
        <CircularProgressBar isloading={loading} setIsloading={setLoading} />
      )}

      {/* Header */}
      <div className="import-pdf-header">
        <div className="header-left">
          <div className="home-icon" onClick={() => navigate("/")}>
            <div className="home-icon-wrapper">
              <HomeIcon />
            </div>
            <div className="home-icon-separator"></div>
            <div className="home-icon-text">
              <div className="header-title">Create e-Detail</div>
              <div className="header-subtitle">e-Detail</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <ModuleStepper currentStep={currentStep} steps={steps} />

      {/* Main Content */}
      <div className="import-pdf-content">
        {/* Section Title */}
        <div className="section-title-text">Select one PDF to continue</div>

        {/* Filters Section - Matching ModuleContent.js structure */}
        <div className="module-content-dropdown">
          <div
            className="module-content-dropdown-list"
            style={{ width: "214px", display: "flex", flexGrow: "1" }}
          >
            <SelectAutoComplete
              disableClearable={false}
              options={brandsList}
              disableValue={false}
              value={tempSelectedProduct}
              label="Product"
              required={false}
              renderOption={templateListUI}
              onChange={handleProductChange}
            />
          </div>
          <div
            className="module-content-dropdown-list"
            style={{ width: "214px", display: "flex", flexGrow: "1" }}
          >
            <SelectAutoComplete
              disableClearable={false}
              options={marketsList}
              disableValue={false}
              value={tempSelectedCountry}
              label="Country"
              required={false}
              renderOption={templateListUI}
              onChange={handleCountryChange}
            />
          </div>
          <div
            className="module-content-dropdown-list"
            style={{ width: "214px", display: "flex", flexGrow: "1" }}
          >
            <SelectAutoComplete
              disableClearable={false}
              options={indicationsList}
              disableValue={false}
              value={tempSelectedIndication}
              label="Indication"
              required={false}
              renderOption={templateListUI}
              onChange={handleIndicationChange}
            />
          </div>
          <div
            className="module-content-dropdown-list"
            style={{ width: "214px", display: "flex", flexGrow: "1" }}
          >
            <div className="mlr-status-filter locked">
              <span className="mlr-status-label">MLR Status</span>
              <span className="mlr-status-value">Approved for Use</span>
            </div>
          </div>
          <div
            className="module-content-dropdown-list"
            style={{ width: "265px" }}
          >
            <SearchTextField
              searchHandler={searchHandler}
              handleEnterKeypress={handleEnterKeypress}
              searchText={searchText}
              cancelSearchField={cancelSearchField}
              searchfieldsHandler={searchfieldsHandler}
            />
          </div>
          <button
            className={`module-content-reset-button ${enableResetBtn
              ? "module-content-active-reset-button"
              : "module-content-deactive-reset-button"
              }`}
            disabled={enableResetBtn === true ? false : true}
            onClick={handleResetFilters}
          >
            RESET FILTERS
          </button>
        </div>

        {/* PDF Cards Grid */}
        <div className="promomats-cards-container">
          <InfiniteScroll
            dataLength={pdfList.length}
            next={() => fetchVeevaDocs(false)}
            hasMore={hasMore}
            scrollableTarget="scrollableDivPromoMats"
            ref={infinteScrollRef}
            scrollThreshold={0.9}
          >
            <div
              className="module-content-card-parent-div"
              id="scrollableDivPromoMats"
            >
              {!loading && pdfList?.length === 0 &&
                <div className="d-flex align-items-center justify-content-center">
                  <img src={NoResultsFound} alt="No results found" />
                </div>
              }
              {pdfList.length > 0 && 
                <div className="module-content-card-child-div">
                  {pdfList.map((pdf, index) => {
                    const isPdf = isPdfFile(pdf.docFilename);
                    return (
                      <div
                        key={pdf.docId || index}
                        className={`pdf-card ${selectedPromoPdf?.docId === pdf.docId ? "selected" : ""
                          } ${!isPdf || pdf.docIsLargeFile ? "disabled" : ""}`}
                        onClick={() => handlePromoPdfSelect(pdf)}
                      >
                        {selectedPromoPdf?.docId === pdf.docId && (
                          <img
                            src={BlueCheckBox}
                            alt=""
                            className="selected-checkmark-icon"
                          />
                        )}
                        <div className="pdf-card-content">
                          <div
                            className="pdf-card-thumbnail"
                            onMouseEnter={() => handleThumbnailHover(pdf.docId)}
                            onMouseLeave={handleThumbnailLeave}
                          >
                            {!isPdf && hoveredPdfId === pdf.docId ? (
                              <div className="pdf-not-available-message">
                                <div className="message-text">
                                  This file cannot be selected. It is not a PDF
                                  file
                                </div>
                              </div>
                            ) : isPdf && hoveredPdfId === pdf.docId && pdf.docIsLargeFile ? (
                              <div className="pdf-not-available-message">
                                <div className="message-text">
                                  This file cannot be selected. The file size is Large.
                                </div>
                              </div>
                            ) : pdfThumbnails[pdf.docId] ? (
                              <>
                                <Document
                                  file={pdfThumbnails[pdf.docId]}
                                  loading={
                                    <div className="pdf-thumbnail-loading">
                                      Loading...
                                    </div>
                                  }
                                  error={
                                    <img
                                      src={CoverIcon}
                                      alt="PDF Cover"
                                      className="pdf-cover-image"
                                    />
                                  }
                                >
                                  <Page
                                    pageNumber={1}
                                    width={240}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                  />
                                </Document>
                                {hoveredPdfId === pdf.docId && isPdf && (
                                  <div className="pdf-thumbnail-overlay">
                                    <div
                                      className="pdf-overlay-preview"
                                      onClick={(e) =>
                                        handleThumbnailClick(e, pdf)
                                      }
                                    >
                                      <img
                                        src={previewEyeIcon}
                                        alt="preview"
                                        style={{
                                          marginRight: "6px",
                                          marginTop: "-2px",
                                        }}
                                        width={12}
                                        height={8}
                                      />
                                      <div className="pdf-preview-text">
                                        Preview
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <img
                                  src={CoverIcon}
                                  alt="PDF Cover"
                                  className="pdf-cover-image"
                                />
                                {hoveredPdfId === pdf.docId && isPdf && (
                                  <div className="pdf-thumbnail-overlay">
                                    <div
                                      className="pdf-overlay-preview"
                                      onClick={(e) =>
                                        handleThumbnailClick(e, pdf)
                                      }
                                    >
                                      <img
                                        src={previewEyeIcon}
                                        alt="preview"
                                        style={{
                                          marginRight: "6px",
                                          marginTop: "-2px",
                                        }}
                                        width={12}
                                        height={8}
                                      />
                                      <div className="pdf-preview-text">
                                        Preview
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="pdf-card-details">
                            <div className="d-flex align-items-center">
                              <img
                                src={
                                  pdf.docStatus === "Approved for Use"
                                    ? GreenTickMark
                                    : NotApprovedImage
                                }
                                alt=""
                              />
                              <CustomTooltip
                                title={pdf?.docDocumentNumber || "-"}
                                classes="module-list-card-brand-name-tooltip"
                              >
                                <a
                                  className={`module-list-card-moduleName ${pdf?.docLink && "hyper-d-link"}`}
                                  href={pdf?.docLink}
                                  target="_blank"
                                  style={{ textDecoration: "none", cursor: "pointer" }}
                                >
                                  {pdf?.docDocumentNumber || "-"}
                                </a>
                              </CustomTooltip>
                            </div>

                            <div className="d-flex align-items-center module-card-brand-details">
                              <h4 className="module-list-card-brand-name">
                                Product:{" "}
                              </h4>
                              <h2 className="module-list-card-brand-content">
                                {pdf.docProductName?.[0] ||
                                  selectedProduct?.name ||
                                  "-"}
                              </h2>
                              {(() => {
                                // Get country name: Priority API name > Mapped name > Code
                                let countryName = pdf.docCountryName?.[0];

                                if (!countryName) {
                                  const pdfCountryCode = pdf.docCountry?.[0];
                                  const countryFromList = pdfCountryCode
                                    ? marketsList.find(
                                      (market) =>
                                        market.code?.toLowerCase() ===
                                        pdfCountryCode.toLowerCase() ||
                                        market.name?.toLowerCase() ===
                                        pdfCountryCode.toLowerCase()
                                    )
                                    : null;
                                  countryName =
                                    countryFromList?.name || pdfCountryCode || null;
                                }

                                return countryName ? (
                                  <>
                                    <span className="module-list-card-separator">
                                      {" "}
                                    </span>
                                    <h4 className="module-list-card-brand-name">&nbsp;&nbsp;&nbsp;Country:{" "}</h4>
                                    <h2 className="module-list-card-brand-content">
                                      {countryName}
                                    </h2>
                                  </>
                                ) : null;
                              })()}
                            </div>

                            {/* Indication Section */}
                            {/* <div className="d-flex align-items-center module-card-brand-details">
                              <h4 className="module-list-card-brand-name">
                                Indication:{" "}
                              </h4>
                              {(() => {
                                let indicationName = pdf.docIndicationName?.[0];

                                if (!indicationName) {
                                  const pdfIndicationId = pdf.docIndication?.[0];
                                  const indicationFromList = pdfIndicationId
                                    ? indicationsList.find(
                                      (ind) =>
                                        ind._id === pdfIndicationId ||
                                        ind.id === pdfIndicationId ||
                                        ind.code === pdfIndicationId
                                    )
                                    : null;
                                  indicationName = indicationFromList?.name || pdfIndicationId || "-";
                                }

                                return (
                                  <CustomTooltip
                                    title={indicationName}
                                    classes="module-list-card-brand-name-tooltip"
                                  >
                                    <h2 className="module-list-card-brand-content">
                                      {indicationName}
                                    </h2>
                                  </CustomTooltip>
                                )
                              })()}
                            </div> */}

                            <div className="d-flex align-items-center module-card-brand-details">
                              <h4 className="module-list-card-brand-name">
                                Name:{" "}
                              </h4>
                              <h2 className="module-list-card-brand-content">
                                {pdf.docName.length > 14 ? (
                                  <CustomTooltip
                                    title={pdf.docName || "-"}
                                    classes="module-list-card-brand-name-tooltip"
                                  >
                                    <span>{pdf.docName}</span>
                                  </CustomTooltip>
                                ) : (pdf.docName || "-")}
                              </h2>
                              {/* <CustomTooltip
                                title={pdf.docName || "-"}
                                classes="module-list-card-brand-name-tooltip"
                              >
                                <h2 className="module-list-card-brand-content">
                                  {pdf.docName || "-"}
                                </h2>
                              </CustomTooltip> */}
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <h2 className="d-flex align-items-center module-list-card-created-at">
                                <img src={time} alt="" />
                                <h4>{daysAgoCreated(pdf.docVersionModifiedDate)}</h4>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              }
            </div>

          </InfiniteScroll>
        </div>

        {/* Footer */}
        <div className="import-pdf-footer">
          <button
            className="btn-cancel"
            onClick={() => setShowConfirmModal(true)}
          >
            CANCEL
          </button>
          <button
            className={`btn-next ${selectedPromoPdf ? "active" : ""}`}
            onClick={handleNext}
            disabled={!selectedPromoPdf}
          >
            NEXT
          </button>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPdfPreview && previewPdfUrl && (
        <Dialog
          open={showPdfPreview}
          className="pdf-preview-modal"
          aria-labelledby="pdf-preview-title"
        >
          <div className="pdf-preview-modal-wrapper">
            <div className="pdf-preview-header">
              <div className="pdf-preview-title">
                <div className="pdf-preview-head">PREVIEW</div>
                <div className="pdf-preview-details">
                  <div className="pdf-preview-detail">
                    <img 
                      src={previewPdfStatus === "Approved for Use"? GreenTickMark : NotApprovedImage } 
                      alt="info"
                      style={{
                        width: "14px",
                        height: "14px",
                        paddingTop: "3px"
                      }}
                    />
                    <div className="pdf-preview-value">{previewPdfDocumentNumber}</div>
                  </div>
                  <div className="pdf-preview-detail">
                    <div className="pdf-preview-name">Name: </div>
                    <div className="pdf-preview-value">{previewPdfName}</div>
                  </div>
                  <div className="pdf-preview-detail">
                    <div className="pdf-preview-name">Product: </div>
                    <div className="pdf-preview-value">{previewPdfProduct}</div>
                  </div>
                  <div className="pdf-preview-detail">
                    <div className="pdf-preview-name">Country: </div>
                    <div className="pdf-preview-value">{previewPdfCountry}</div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="pdf-preview-content"
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                if (scrollTop + clientHeight >= scrollHeight - 300) {
                  setVisiblePreviewPages((prev) =>
                    Math.min(prev + 2, previewTotalPages)
                  );
                }
              }}
            >
              <Document
                file={previewPdfUrl}
                onLoadSuccess={({ numPages }) => {
                  console.log("PDF loaded in modal, total pages:", numPages);
                  setPreviewTotalPages(numPages);
                  setVisiblePreviewPages(3)
                }}
                loading={
                  <div className="pdf-preview-loading">Loading PDF...</div>
                }
                error={
                  <div className="pdf-preview-error">Failed to load PDF</div>
                }
              >
                {visiblePreviewPages > 0
                  ? Array.from(
                    { length: visiblePreviewPages },
                    (_, index) => index + 1
                  ).map((pageNum) => (
                    <Page
                      key={`modal-page-${pageNum}`}
                      pageNumber={pageNum}
                      width={800}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={
                        <div className="page-loading">
                          Loading page {pageNum}...
                        </div>
                      }
                      error={null}
                    />
                  ))
                  : null}
              </Document>
            </div>

            <div className="pdf-preview-footer">
              <Button
                onClick={closePdfPreview}
                className="pdf-preview-close-btn"
              >
                CLOSE
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={() => {
          navigate("/");
        }}
        title="ARE YOU SURE GO BACK TO THE DASHBOARD"
        message="If you go back to the dashboard, you will exit the flow and return to the dashboard."
        confirmText="YES"
        cancelText="NO"
      />
    </div>
  );
};

export default ImportPDFPage;
